// Pack 0 and 1 cell drawings, under the grid lines: shaded regions (extra regions, fortress,
// minimum/maximum, magic squares, clones) and odd circles / even squares.
import type { ConstraintEntry, PuzzleDoc, Rc } from '@zinc/shared';
import { cellAt, type Geometry } from '../../../lib/geometry';
import { CELL, centre, isRc, num, ofType, rcKey, rcList, styleOf, x0, y0 } from './geom';
import {
  CLONE_SHADES,
  ENDS,
  EVEN_SIDE,
  FONT,
  INK,
  KROPKI,
  ODD_RADIUS,
  SHADES,
  SIGN,
  WHITE,
  type Shade,
} from './style';

/** One path covering the cells, so neighbouring cells shade without seams. */
export function cellsPath(cells: Rc[]): string {
  return cells.map(([r, c]) => `M${x0(c)} ${y0(r)}h${CELL}v${CELL}h${-CELL}Z`).join('');
}

/** A shade with a per-entry `style.color` override (opacity stays). */
function shadeOf(entry: Parameters<typeof styleOf>[0], shade: Shade): Shade {
  const { color } = styleOf(entry, { color: shade.color, width: 1 });
  return { color, opacity: shade.opacity };
}

function Shaded({ type, cells, shade }: { type: string; cells: Rc[]; shade: Shade }) {
  if (cells.length === 0) return null;
  return (
    <path
      data-constraint={type}
      d={cellsPath(cells)}
      fill={shade.color}
      fillOpacity={shade.opacity}
    />
  );
}

/**
 * A chevron with its point at (x, y), pointing along (dx, dy) (a unit vector). Also used by
 * greater-than signs.
 */
export function chevron(x: number, y: number, dx: number, dy: number, arm: number): string {
  const bx = x - dx * arm * 0.8;
  const by = y - dy * arm * 0.8;
  return `M${bx - dy * arm} ${by + dx * arm}L${x} ${y}L${bx + dy * arm} ${by - dx * arm}`;
}

const SIDES: [number, number][] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/**
 * Minimum and maximum cells: a light shade and a small chevron inside each edge that faces a
 * neighbour outside the entry. Chevrons point at the smaller cell, as greater-than signs do:
 * inwards for a minimum, outwards for a maximum.
 */
function Extremum({
  type,
  cells,
  g,
  shade,
}: {
  type: 'minimum' | 'maximum';
  cells: Rc[];
  g: Geometry;
  shade: Shade;
}) {
  const own = new Set(cells.map(([r, c]) => rcKey(r, c)));
  const parts: string[] = [];
  const inset = 5;
  for (const rc of cells) {
    const [cx, cy] = centre(rc);
    for (const [dr, dc] of SIDES) {
      const nr = rc[0] + dr;
      const nc = rc[1] + dc;
      if (own.has(rcKey(nr, nc)) || cellAt(g, nr, nc) < 0) continue;
      // Centre of the chevron, just inside this edge.
      const mx = cx + dc * (CELL / 2 - inset);
      const my = cy + dr * (CELL / 2 - inset);
      const sign = type === 'minimum' ? -1 : 1;
      const tip = SIGN.arm * 0.4;
      parts.push(
        chevron(mx + sign * dc * tip, my + sign * dr * tip, sign * dc, sign * dr, SIGN.arm * 0.8),
      );
    }
  }
  return (
    <g data-constraint={type}>
      <path d={cellsPath(cells)} fill={shade.color} fillOpacity={shade.opacity} />
      <path
        d={parts.join('')}
        fill="none"
        stroke={SIGN.color}
        strokeWidth={SIGN.width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

export function CellShading({ doc, g }: { doc: PuzzleDoc; g: Geometry }) {
  return (
    <g>
      {ofType(doc, 'extra-region').map((e, i) => (
        <Shaded
          key={`x${i}`}
          type="extra-region"
          cells={rcList(e)}
          shade={shadeOf(e, SHADES['extra-region'])}
        />
      ))}
      {/* Rows and columns only ("within every box ...") is a rule in words: not shaded. */}
      {ofType(doc, 'magic-square')
        .filter((e) => e.diagonals !== false)
        .map((e, i) => (
          <Shaded
            key={`m${i}`}
            type="magic-square"
            cells={rcList(e)}
            shade={shadeOf(e, SHADES['magic-square'])}
          />
        ))}
      {ofType(doc, 'clone').map((e, i) => {
        const shade = shadeOf(e, CLONE_SHADES[i % CLONE_SHADES.length]!);
        return (
          <Shaded
            key={`c${i}`}
            type="clone"
            cells={[...rcList(e), ...rcList(e, 'copy')]}
            shade={shade}
          />
        );
      })}
      {ofType(doc, 'fortress').map((e, i) => (
        <Shaded
          key={`f${i}`}
          type="fortress"
          cells={rcList(e)}
          shade={shadeOf(e, SHADES.fortress)}
        />
      ))}
      {ofType(doc, 'minimum', 'maximum').map((e, i) => (
        <Extremum
          key={`e${i}`}
          type={e.type as 'minimum' | 'maximum'}
          cells={rcList(e)}
          g={g}
          shade={shadeOf(e, SHADES.extremum)}
        />
      ))}
    </g>
  );
}

/** Odd: a pale grey circle; even: a pale grey square. */
export function OddEven({ doc }: { doc: PuzzleDoc }) {
  return (
    <g>
      {ofType(doc, 'odd', 'even').map((e, i) => {
        const { color, opacity } = shadeOf(e, SHADES.parity);
        return (
          <g key={i} data-constraint={e.type} fill={color} fillOpacity={opacity}>
            {rcList(e).map((rc) => {
              const [x, y] = centre(rc);
              return e.type === 'odd' ? (
                <circle key={rcKey(...rc)} cx={x} cy={y} r={ODD_RADIUS} />
              ) : (
                <rect
                  key={rcKey(...rc)}
                  x={x - EVEN_SIDE / 2}
                  y={y - EVEN_SIDE / 2}
                  width={EVEN_SIDE}
                  height={EVEN_SIDE}
                />
              );
            })}
          </g>
        );
      })}
    </g>
  );
}

/** The cells a shading circle stands on: a view count's cell, an area clue's cells. */
function circled(e: ConstraintEntry): Rc[] {
  return e.type === 'view-count' ? (isRc(e.cell) ? [e.cell] : []) : rcList(e);
}

/**
 * Pack 6's circled cells (a view count, an area's clue), drawn like the source: a white circle
 * with an ink border (Marko, FOR_MARKO #18). It lies beneath the player's colours, so shading a
 * circled cell tints it as in the source.
 */
export function ShadingCircles({ doc }: { doc: PuzzleDoc }) {
  return (
    <g fill={WHITE} stroke={INK} strokeWidth={KROPKI.strokeWidth} pointerEvents="none">
      {ofType(doc, 'area-clues', 'view-count').map((e, i) => (
        <g key={i} data-constraint={e.type}>
          {circled(e).map((rc) => {
            const [x, y] = centre(rc);
            return <circle key={rcKey(...rc)} cx={x} cy={y} r={ENDS.circle * 1.6} />;
          })}
        </g>
      ))}
    </g>
  );
}

/**
 * Marked cells (Pack 8): friendly cells and diversity circles as a grey ring, box-indexing
 * cells as a grey square outline (ENDS: the between-line circles' grey), Pack 9's counting
 * circles as the same ring. A view count's given number sits in its circle (`ShadingCircles`)
 * like a quadruple's digit.
 */
export function CellMarks({ doc }: { doc: PuzzleDoc }) {
  return (
    <g fill="none" stroke={ENDS.color} strokeWidth={ENDS.strokeWidth}>
      {ofType(doc, 'friendly', 'diversity', 'box-indexer', 'view-count', 'counting-circles').map(
        (e, i) => {
          const circle = e.type === 'view-count';
          const at = circle ? circled(e) : rcList(e);
          const value = circle ? num(e, 'value') : null;
          return (
            <g key={i} data-constraint={circle ? 'view-count-value' : e.type}>
              {!circle &&
                at.map((rc) => {
                  const [x, y] = centre(rc);
                  const r = ENDS.circle * 1.6;
                  return e.type === 'box-indexer' ? (
                    <rect key={rcKey(...rc)} x={x - r} y={y - r} width={r * 2} height={r * 2} />
                  ) : (
                    <circle key={rcKey(...rc)} cx={x} cy={y} r={r} />
                  );
                })}
              {value !== null &&
                at.map((rc) => {
                  const [x, y] = centre(rc);
                  return (
                    <text
                      key={`v${rcKey(...rc)}`}
                      x={x}
                      y={y + 0.5}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontFamily={FONT}
                      fontSize={12}
                      fontWeight={600}
                      fill={INK}
                      stroke="none"
                    >
                      {value}
                    </text>
                  );
                })}
            </g>
          );
        },
      )}
    </g>
  );
}
